import { isFunction, pluralize, upperCamelCase } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Menu,
  MenuItem,
  Pagination,
  SortDirection,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { JSX, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { usePagination } from '../State';
import { LAYOUT2, TYPOGRAPHY2 } from '../Styles';

const DATA_TABLE_FOOTER_STYLE = css`
  label: DATA_TABLE_FOOTER_STYLE;
  padding: 12px;
  color: var(--text2);
  background: var(--background);
  border-top: 1px solid var(--border2);
  position: sticky;
  bottom: 0;
  z-index: 1;
  ${TYPOGRAPHY2.xSmall}
`;

export type ColumnType = undefined | string | number | JSX.Element;
export type BuiltInSortMethod = 'date' | 'number';
export type DataTableItem = Record<string, any> & {
  link?: string;
  id?: string;
  menu?: Array<{
    label: string;
    action: () => void;
  }>;
};
export type DataTableColumn = {
  name: string;
  displayName?: string; // if not provided, the name will be used (upperCamelCase with ' ' as the separator)
  sortable?: boolean; // if true, the column will be sortable
  sortMethod?: BuiltInSortMethod | ((a: string, b: string) => number);
  format?: (value: any) => string | null | undefined | JSX.Element;
  width?: string | number;
  truncate?: boolean; // if true, text will be truncated with ellipsis; use with width to constrain the column
};

const compareDateFields = (a: any, b: any, field: string) => {
  return new Date(b[field]).getTime() - new Date(a[field]).getTime();
};

const extractNumberRegex = /[-+]?[,\d]*\.?\d+(?:[eE][-+]?\d+)?/g;

const extractNumber = (value: string | undefined | null) => {
  if (!value) return undefined;
  const match = value.match(extractNumberRegex);
  if (match) return Number(match[0].replace(',', ''));
  return undefined;
};

const compareNumberFields = (a: any, b: any, field: string) => {
  const aNumber = extractNumber(a[field]);
  const bNumber = extractNumber(b[field]);
  if (aNumber && bNumber) return bNumber - aNumber;
  if (aNumber === bNumber) return 0;
  if (aNumber) return -1;
  if (bNumber) return 1;
  return 0;
};

const existingSortMethod = (a: any, b: any, field: string, normalComparator: (a: any, b: any, field: string) => number) => {
  const aExists = a[field] != null;
  const bExists = b[field] != null;
  if (aExists && bExists) return normalComparator(a, b, field);
  if (aExists === bExists) return 0;
  if (aExists) return -1;
  if (bExists) return 1;
  return 0;
};

const descendingComparator = <T,>(a: T, b: T, orderBy: keyof T): number => {
  const aVal = a[orderBy];
  const bVal = b[orderBy];

  if (typeof aVal === 'string' && typeof bVal === 'string') {
    return bVal.localeCompare(aVal, undefined, { sensitivity: 'base' });
  }
  if (typeof aVal === 'number' && typeof bVal === 'number') return bVal - aVal;
  return 0;
};

const getSortMethodFunction = (column: DataTableColumn) => {
  if (column.sortMethod === 'date') return compareDateFields;
  if (column.sortMethod === 'number') return compareNumberFields;
  if (isFunction(column.sortMethod)) return column.sortMethod;
  if (column.sortMethod) console.warn(`Unknown sort method: ${column.sortMethod}`);
  return descendingComparator;
};

const getSortedItems = <T extends { [key: string]: ColumnType }>(
  items: T[],
  descending: boolean,
  orderBy: string,
  column: DataTableColumn,
) => {
  const ascendingComparator = getSortMethodFunction(column);
  return [...items].sort((a, b) => (descending ? 1 : -1) * existingSortMethod(a, b, orderBy, ascendingComparator));
};

const TABLE_STYLE = css`
  label: TABLE_STYLE;
  background: var(--background2);
  border-collapse: collapse;
  position: relative;
  width: 100%;

  td {
    padding: ${LAYOUT2.spacing1}px ${LAYOUT2.spacing1}px ${LAYOUT2.spacing1}px 0;
    color: var(--text);

    &:first-of-type {
      padding-left: ${LAYOUT2.spacing1}px;
    }

    &.cell {
      &--email {
        text-transform: none;
      }
    }
  }

  // bottom border on the thead that survives scrolling
  thead:after {
    background: var(--border2);
    content: " ";
    display: block;
    height: 1px;
    margin-top: -1px;
    position: absolute;
    width: 100%;
  }

  th {
    background-color: var(--background);
    color: var(--text2);
    ${TYPOGRAPHY2.xSmall}
    line-height: 2;
    min-height: 50px;
    height: 50px;
    max-height: 50px;
    padding: 0;
    text-align: left;
    text-transform: capitalize;
    position: sticky;
    top: 0;
    z-index: 1;

    &:first-of-type {
      padding-left: 20px;
    }

    // Override Material-UI active sort label color
    .MuiTableSortLabel-root.Mui-active {
      color: var(--text) !important;
    }

    // Override Material-UI sort label hover color
    .MuiTableSortLabel-root:hover {
      color: var(--text) !important;
    }

    // Override Material-UI sort icon color
    .MuiTableSortLabel-icon {
      color: var(--text2) !important;
    }

    .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon {
      color: var(--text) !important;
    }

    .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon {
      color: var(--text) !important;
    }
  }

  td {
    border-bottom: none !important;
  }

  .row,
  tr {
    border-bottom: solid 1px var(--border2) !important;
    background-color: var(--background);
    ${TYPOGRAPHY2.xSmall}
    line-height: 1.667;

    &:last-of-type {
      border-bottom: none;
    }

    &:nth-of-type(odd) {
      background-color: var(--background2);
    }

    &--head {
      color: var(--text2);
      ${TYPOGRAPHY2.xSmall}
      padding: 0;
    }

    &[data-clickable="true"] {
      cursor: pointer;
      &:hover {
        background-color: var(--background);
      }
    }
  }
`;

const MENU_BUTTON_STYLE = css`
  label: data-table-menu-button;
  cursor: pointer;
  padding: 4px;
  color: var(--text2);
  transition: color 0.2s;
  &:hover {
    color: var(--text);
  }
`;

const MenuButton = ({
  menu,
  rowId,
}: {
  menu: Array<{ label: string; action: () => void }>;
  rowId: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    if (event) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = (event: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (event && 'stopPropagation' in event) {
      (event as React.MouseEvent).stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: () => void) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    action();
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      <button css={MENU_BUTTON_STYLE} onClick={handleClick}>
        <MoreVertIcon />
      </button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={e => e.stopPropagation()}
      >
        {menu.map((item, index) => (
          <MenuItem key={`menu-item-${index}`} onClick={handleMenuItemClick(item.action)}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

const DATA_TABLE_CONTAINER_STYLE = css`
  label: DATA_TABLE_CONTAINER_STYLE;
  overflow: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  border: 1px solid var(--border3);
`;

export const DataTable = ({
  items,
  columns,
  defaultOrder,
  defaultOrderBy,
  onSortChange,
  recordRenderMax = 100,
  recordType = 'record',
  pageSize = 100,
  showPagination = false,
}: {
  items: DataTableItem[];
  columns: DataTableColumn[];
  defaultOrder?: SortDirection;
  defaultOrderBy?: string;
  // Fired whenever the user picks a new sort column or direction — this table
  // stays uncontrolled (it still owns sortOrder/orderByColumnName itself), so a
  // host that wants to remember the choice reads it here rather than owning it.
  onSortChange?: (sortOrder: 'asc' | 'desc', orderByColumnName: string) => void;
  recordRenderMax?: number;
  recordType?: string;
  pageSize?: number;
  showPagination?: boolean;
}) => {
  if (columns.length === 0) columns = [{ name: 'noColumnsProvided' }];

  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState<SortDirection>(defaultOrder ?? 'asc');
  const [orderByColumnName, setOrderByColumnName] = useState<string>(
    defaultOrderBy || columns[0].name,
  );

  const sortColumn = columns.find(c => c.name === orderByColumnName) ?? columns[0];
  const sortedItems = useMemo(
    () => getSortedItems(items, sortOrder === 'desc', orderByColumnName, sortColumn),
    [items, sortOrder, orderByColumnName, sortColumn],
  );

  // Use pagination hook
  const {
    currentPage,
    setCurrentPage,
    totalPages,
    startIndex,
    endIndex,
    paginatedItems,
  } = usePagination({
    pageSize,
    totalItems: sortedItems.length,
    items: sortedItems,
  });

  const hasMenu = items.some(item => item.menu && item.menu.length > 0);

  return (
    <div css={[DATA_TABLE_CONTAINER_STYLE]}>
      <table css={TABLE_STYLE} className='table' style={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            {columns.map((column, index) => {
              return (
                <TableCell
                  key={column.name}
                  sortDirection={orderByColumnName === column.name ? sortOrder : false}
                  width={column.width}
                >
                  {column.sortable
                    ? (
                      <TableSortLabel
                        active={orderByColumnName === column.name}
                        direction={(orderByColumnName === column.name && sortOrder) || 'asc'}
                        onClick={(e: React.MouseEvent<unknown>) => {
                          const isAsc = orderByColumnName === column.name && sortOrder === 'asc';
                          const nextSortOrder = isAsc ? 'desc' : 'asc';
                          setSortOrder(nextSortOrder);
                          setOrderByColumnName(column.name);
                          onSortChange?.(nextSortOrder, column.name);
                        }}
                      >
                        {column.displayName ?? upperCamelCase(column.name, ' ')}
                      </TableSortLabel>
                    )
                    : <span>{column.displayName ?? upperCamelCase(column.name, ' ')}</span>}
                </TableCell>
              );
            })}
            {hasMenu && <TableCell width='48px' />}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedItems.map((row, index) => {
            const isClickable = !!row.link;
            return (
              <TableRow
                key={row.id ?? `row-${index}`}
                sx={{
                  cursor: isClickable ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: isClickable
                      ? `var(--background3) !important`
                      : undefined,
                  },
                }}
                onClick={isClickable
                  ? () => {
                    if (row.link) navigate(row.link);
                  }
                  : undefined}
              >
                {columns.map((column, idx) => {
                  const cellContent = column.format ? column.format(row[column.name]) : row[column.name];
                  return (
                    <TableCell key={`cell-${idx}`} style={column.truncate ? { maxWidth: column.width ?? 200, overflow: 'hidden' } : undefined}>
                      {column.truncate
                        ? (
                          <div
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={typeof cellContent === 'string' ? cellContent : undefined}
                          >
                            {cellContent}
                          </div>
                        )
                        : cellContent}
                    </TableCell>
                  );
                })}
                {hasMenu && (
                  <TableCell>
                    {row.menu && row.menu.length > 0 && <MenuButton menu={row.menu} rowId={row.id ?? `row-${index}`} />}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </table>
      <div css={DATA_TABLE_FOOTER_STYLE}>
        {showPagination && totalPages > 1
          ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {pluralize(sortedItems.length, recordType)}
                {sortedItems.length > pageSize && (
                  `, showing ${startIndex + 1}-${Math.min(endIndex, sortedItems.length)}`
                )}
              </div>
              <Pagination
                count={totalPages}
                page={currentPage + 1} // MUI uses 1-based indexing
                onChange={(_, page) => setCurrentPage(page - 1)} // Convert back to 0-based
                color='primary'
                size='small'
                showFirstButton
                showLastButton
              />
            </div>
          )
          : (
            <>
              {pluralize(sortedItems.length, recordType)}
              {sortedItems.length > recordRenderMax && (
                ', showing first ' + recordRenderMax + ' (click on columns to sort and see more)'
              )}
            </>
          )}
      </div>
    </div>
  );
};
